import React from 'react';
import { ShoppingCart as ShoppingCartIcon, Minus, Plus, Trash2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { CartItem, CartTotals, PaymentMethod } from '../types/pos.types';
import { TaxCalculator } from '@/config/tax';

interface MobileCartSheetProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  totals: CartTotals;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemoveItem: (id: string) => void;
  onClearCart: () => void;
  onCheckout: (paymentMethod: PaymentMethod) => void;
}

export const MobileCartSheet: React.FC<MobileCartSheetProps> = ({
  isOpen,
  onClose,
  items,
  totals,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onCheckout
}) => {
  const handleCheckout = (paymentMethod: PaymentMethod) => {
    onCheckout(paymentMethod);
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[85vh] flex flex-col bg-green-50">

        {items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <ShoppingCartIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">Carrito vacío</p>
              <p className="text-sm">Agrega productos para continuar</p>
            </div>
          </div>
        ) : (
          <>
            {/* Cart Items */}
            <div className="flex-1 overflow-auto space-y-3">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="bg-gray-50 rounded-lg p-4"
                >
                  {/* Primera fila: Nombre del producto + presentación + eliminar */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900 flex-1">
                      {item.product.name}
                      <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
                        {item.product.presentation ? item.product.presentation.slice(0, 3).toUpperCase() : 'STD'}
                      </span>
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveItem(item.id)}
                      className="text-red-500 hover:text-red-700 p-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Segunda fila: Precio unitario */}
                  <p className="text-sm text-gray-500 mb-3">S/ {item.product.price.toFixed(2)} c/u</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onUpdateQuantity(item.id, Math.max(0, item.quantity - 1))}
                        className="w-8 h-8 p-0"
                        disabled={item.quantity <= 1}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      
                      <Badge variant="secondary" className="px-3 py-1 text-sm font-semibold">
                        {item.quantity}
                      </Badge>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 p-0"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="text-right">
                      <p className="font-bold text-lg">S/ {item.subtotal.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Totals and Actions */}
            <div className="border-t pt-4 space-y-4 bg-white">
              {/* Total */}
              <div className="flex justify-between font-bold text-2xl mb-4">
                <span>Total:</span>
                <span className="text-green-600">S/ {totals.total.toFixed(2)}</span>
              </div>

              {/* Action buttons */}
              <div className="grid grid-cols-1 gap-3">
                <Button
                  onClick={() => handleCheckout('efectivo')}
                  className="h-12 text-lg font-semibold bg-rose-600 hover:bg-rose-700"
                  size="lg"
                >
                  Pagar en Efectivo
                </Button>

                <div className="grid grid-cols-3 gap-2">
                  <Button
                    onClick={() => handleCheckout('tarjeta')}
                    variant="outline"
                    className="h-10"
                  >
                    Tarjeta
                  </Button>
                  <Button
                    onClick={() => handleCheckout('yape')}
                    variant="outline"
                    className="h-10 bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200 hover:border-purple-300"
                  >
                    Yape/Plin
                  </Button>
                  <Button
                    onClick={() => handleCheckout('aplicativo')}
                    variant="outline"
                    className="h-10 bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200 hover:border-orange-300"
                  >
                    Delivery
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};